import type {ChecklistData, ChecklistToc, CollectionDataResponse} from "../types";
import {ChecklistItemType, Status} from "../types";
import {getLeafNodeIdsFromToc} from "./utils";

export const getLeafNodeStatuses = (
    leafNodeIds: string[],
    collectionData: CollectionDataResponse,
): Set<Status> => {
    const statuses = new Set<Status>();
    for (const id of leafNodeIds) {
        statuses.add(
            collectionData?.checkitems[id]?.status ?? Status.todo,
        );
    }
    return statuses;
};

export const shouldShowConfirmation = (
    isOpen: boolean,
    statusToSet: Status,
    leafNodeStatuses: Set<Status>,
): boolean => {
    if (isOpen || statusToSet === Status.na || statusToSet === Status.todo) {
        return false;
    }
    if (leafNodeStatuses.size !== 1) {
        return true;
    }
    const status = Array.from(leafNodeStatuses)[0];
    return status !== Status.ok && status !== Status.deviation;
};

export const handleParentStatusClick = ({
    id,
    statusToSet,
    viewFilteredToc,
    openItems,
    collectionData,
    patchCheckItems,
    openConfirmCheckAll,
}: {
    id: string;
    statusToSet: Status;
    viewFilteredToc: ChecklistToc;
    openItems: Record<string, boolean>;
    collectionData: CollectionDataResponse;
    patchCheckItems: (itemIds: string[], status: Status) => void;
    openConfirmCheckAll: (data: {
        status: Status;
        leafNodeIds: string[];
    }) => void;
}): void => {
    const node = viewFilteredToc[id];
    if (!node) return;

    const leafNodeIds = getLeafNodeIdsFromToc(viewFilteredToc, id);
    if (leafNodeIds.length === 0) return;

    const isOpen = openItems[id] || false;
    const leafNodeStatuses = getLeafNodeStatuses(leafNodeIds, collectionData);

    if (shouldShowConfirmation(isOpen, statusToSet, leafNodeStatuses)) {
        openConfirmCheckAll({status: statusToSet, leafNodeIds});
    } else {
        patchCheckItems(leafNodeIds, statusToSet);
    }
};

export const handleCheckItemStatusClick = ({
    id,
    status,
    collectionData,
    viewFilteredToc,
    openItems,
    openCreateFilling,
    patchCheckItems,
    openConfirmCheckAll,
}: {
    id: string;
    status?: Status;
    checklistData: ChecklistData | null | undefined;
    collectionData: CollectionDataResponse | undefined;
    viewFilteredToc: ChecklistToc;
    openItems: Record<string, boolean>;
    openCreateFilling: () => void;
    patchCheckItems: (itemIds: string[], status: Status) => void;
    openConfirmCheckAll: (data: {
        status: Status;
        leafNodeIds: string[];
    }) => void;
}): void => {
    if (!collectionData) {
        openCreateFilling();
        return;
    }

    const tocNode = viewFilteredToc[id];
    if (!tocNode) return;

    const statusToSet = status ?? Status.todo;

    switch (tocNode.checkItemType) {
        case ChecklistItemType.CheckItemParent:
            handleParentStatusClick({
                id,
                statusToSet,
                viewFilteredToc,
                openItems,
                collectionData,
                patchCheckItems,
                openConfirmCheckAll,
            });
            break;
        case ChecklistItemType.CheckItem:
            patchCheckItems([id], statusToSet);
            break;
        case ChecklistItemType.CheckItemInfo:
            break;
    }
};
